import { getCartIdFromURL } from '../../../../common/constants/guest-checkout-api';
import { isGuestCheckoutPage } from '../../../../common/environment';
import { isMagentoCartInvalid } from '../../../../common/utils/cart-util';
import { isCheckoutPage, retryApiCall } from '../../../../common/utils/util';
import store, { waitForCustomerLogin, waitForUser } from '../../../../store/store';
import { StoreState } from '../../../../store/store.interface';
import { mapMagentoTaxResponse } from '../../../checkout/shipping-methods.mapping';
import { calculateIntlTaxAmount } from '../../../checkout/shop-checkout';
import { getCustomerToken, isCustomerLoginSuccess, loadCustomer } from '../../../customer/customer';
import {
  getGuestCartItemsMagentoApiCall,
  getLoggedInCartItemsMagentoApiCall,
  getLoggedInCartItemsTotalsMagentoApiCall,
  getLoggedInCartItemsTotalsMagentoApiCallGuestCheckout,
} from '../api/shop-cart.api';
import { ShopCart } from '../interfaces/shop-cart.interface';
import { mapMagentoCartResponseToShopCart } from '../mapping/shop-cart.mapping';
import { getGuestCartToken, getLoggedInCartQuoteId } from '../store/shop-cart.selectors';
import { unsetGuestCartToken } from '../store/shop-cart.store';
import { getGuestCartTokenMagento } from './get-guest-cart-token';
import { getLoggedInCartQuoteIdMagento } from './get-loggedin-cart.thunk';
import { mergeGuestCart } from './merge-guest-cart.thunk';

export async function getTokenFromMagento(thunkAPI, forceUpdate = false) {
  await waitForCustomerLogin();
  const state = thunkAPI.getState() as StoreState;
  if (isCustomerLoginSuccess(state)) {
    await thunkAPI.dispatch(getLoggedInCartQuoteIdMagento());
  } else {
    await thunkAPI.dispatch(getGuestCartTokenMagento({ forceUpdate: forceUpdate }));
  }
}

export async function getCartItemMagento(state: StoreState): Promise<ShopCart> {
  const guestCheckoutPage = isGuestCheckoutPage()
  await waitForCustomerLogin();
  if (isCustomerLoginSuccess(state) && !guestCheckoutPage) {
    const cartQuoteId = getLoggedInCartQuoteId(state);
    const customerToken = getCustomerToken(state);
    if (cartQuoteId) {
      const requestFullCart = isCheckoutPage();
      /*
          Update the magento mini-cart to return the custom_store_code as that is being used
          to define if the users is accessing another page with a different store.
          Once that is done, we can pass the `requestFullCart` variable as the second argument
          to getLoggedInCartItemsMagentoApiCall
        */
      const magentoCart = await getLoggedInCartItemsMagentoApiCall(customerToken, true);

      let totals;
      if (requestFullCart && !isMagentoCartInvalid(magentoCart)) {
        totals = await getLoggedInCartItemsTotalsMagentoApiCall(customerToken);
        if (totals?.tax_amount) {
          const taxInfo = mapMagentoTaxResponse(totals);
          store.dispatch(calculateIntlTaxAmount(taxInfo));
        }
      }

      return mapMagentoCartResponseToShopCart(magentoCart, totals);
    } else {
      throw new Error('quoteId not defined');
    }
  } else if (guestCheckoutPage) {
    return getCartGuestCheckout();
  } else {
    return getCartGuestUser();
  }
}

async function getCartGuestCheckout() {
  const cartId = new URLSearchParams(window.location.search).get('cartId');

  if (cartId) {
    let totals;
    const magentoCart = await getGuestCartItemsMagentoApiCall(cartId);
    // Totals is required as well to get the currency code, as that is not present
    // on the cart response. Could we use the currency properties from the cart response?;
    totals = await getLoggedInCartItemsTotalsMagentoApiCallGuestCheckout(getCartIdFromURL());
    
    if (totals?.tax_amount) {
      const taxInfo = mapMagentoTaxResponse(totals);
      store.dispatch(calculateIntlTaxAmount(taxInfo));
    }

    return mapMagentoCartResponseToShopCart(magentoCart, totals);
  } else {
    console.warn(`Redirecting user to home page as there's no cartId present on the URL for 
          the guest checkout.`);
    location.href = '/';
    throw new Error('Cart id not found');
  }
}

async function getCartGuestUser() {
  const guestCartToken = getGuestCartToken(store.getState() as StoreState);
  if (guestCartToken) {
    const magentoCart = await getGuestCartItemsMagentoApiCall(guestCartToken);
    return mapMagentoCartResponseToShopCart(magentoCart);
  } else {
    throw new Error('magentoToken not defined');
  }
}

export async function getMagentoCart(state) {
  await store.dispatch(loadCustomer());
  await getTokenFromMagento(store);
  await waitForCustomerLogin();
  if(isCustomerLoginSuccess(state) && getGuestCartToken(state)) {
    await store.dispatch(mergeGuestCart(null as any));
  }
  const magentoCart = await retryApiCall(() => getLoggedInCartItemsMagentoApiCall(getCustomerToken(state), true), async () => {
    await store.dispatch(unsetGuestCartToken());
  });
  return mapMagentoCartResponseToShopCart(magentoCart);
}